/**
 * Logger utility for consistent logging across the application
 * Automatically guards console statements in production builds
 *
 * Best Practice: Use this instead of console.log/error/warn directly
 * to prevent leaking sensitive data in production
 */

const isDev = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Log info messages (only in development)
 */
export const log = (...args: unknown[]): void => {
  if (isDev) {
    console.log('[INFO]', ...args);
  }
};

/**
 * Log warning messages (only in development)
 */
export const warn = (...args: unknown[]): void => {
  if (isDev) {
    console.warn('[WARN]', ...args);
  }
};

/**
 * Log error messages (always logged, but sanitized in production)
 */
export const error = (...args: unknown[]): void => {
  if (isDev) {
    console.error('[ERROR]', ...args);
  } else {
    // In production, only log error type and message, not full stack traces
    const sanitized = args.map((arg) => {
      if (arg instanceof Error) {
        return {
          message: arg.message,
          name: arg.name,
          // Don't include stack trace in production
        };
      }
      if (arg && typeof arg === 'object') {
        // Remove sensitive fields from objects
        const { password, token, accessToken, refreshToken, ...safe } =
          arg as Record<string, unknown>;
        void password;
        void token;
        void accessToken;
        void refreshToken;
        return safe;
      }
      return arg;
    });
    console.error('[ERROR]', ...sanitized);
  }
};

/**
 * Log debug messages (only in development)
 */
export const debug = (...args: unknown[]): void => {
  if (isDev) {
    console.debug('[DEBUG]', ...args);
  }
};

/**
 * Log to error tracking service (e.g., Sentry, LogRocket)
 *
 * TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
 * When implementing:
 * 1. Install error tracking SDK (e.g., @sentry/react)
 * 2. Initialize in main.tsx
 * 3. Replace console.error with SDK capture method
 * Example: Sentry.captureException(error, { extra: context });
 */
export const logToErrorTracking = (
  error: unknown,
  context: Record<string, unknown> = {},
): void => {
  if (isProduction) {
    // Error tracking integration pending - see TODO above
    // For now, errors are logged via console in development only
    void error;
    void context;
  } else {
    console.error('[ERROR TRACKING]', error, context);
  }
};
